<template>
  <form
    class="lg:mb-2 lg:flex lg:items-baseline"
    @submit.prevent="applyFilters"
  >
    <div class="grid-standard grow">
      <RevInputText
        id="filter-sourcing-order-id"
        v-model="sourcingOrderId"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.orderId)"
        :value="sourcingOrderId"
      />

      <RevInputText
        id="filter-tracking-number"
        v-model="trackingNumber"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.trackingNumber)"
      />

      <RevInputText
        v-if="showFilterByName"
        id="filter-tracking-customer"
        v-model="customer"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.customer)"
      />

      <RevInputDate
        v-if="showMoreFilters"
        id="filter-creation-date-min"
        v-model="creationDateMin"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.creationDateMin)"
        type="date"
      />

      <RevInputDate
        v-if="showMoreFilters"
        id="filter-creation-date-max"
        v-model="creationDateMax"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.creationDateMax)"
        type="date"
      />

      <RevInputSelect
        v-if="showMoreFilters"
        id="filter-market"
        v-model="countryCode"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.market)"
        :options="availableMarkets"
      />

      <RevInputSelect
        v-if="showTrackingStatus"
        id="filter-tracking-status"
        v-model="status"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.status)"
        :options="statusOptions"
      />

      <RevInputSelect
        v-if="showSuspendReason"
        id="filter-tracking-suspend-reason"
        v-model="suspendReason"
        class="mb-20 md:col-span-2 lg:col-span-4"
        :label="i18n(translations.suspendReason)"
        :options="suspendReasonOptions"
      />
    </div>

    <div class="m-0 ml-auto text-right lg:ml-32 lg:flex">
      <RevLink
        class="mr-24"
        data-qa="filter-reset"
        type="button"
        @click="resetFilters"
      >
        {{ i18n(translations.reset) }}
      </RevLink>

      <RevButton
        data-qa="filter-apply"
        :disabled="!hasSomeFilters"
        type="submit"
        variant="primary"
      >
        {{ i18n(translations.apply) }}
      </RevButton>
    </div>
  </form>

  <RevButton
    v-if="isAllTab"
    size="small"
    variant="secondary"
    @click="toggleMoreFilters"
  >
    {{ i18n(seeMoreFilters ? translations.seeLess : translations.seeMore) }}
  </RevButton>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevInputDate } from '@ds/components/InputDate'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'

import { useStatus } from '../../composables/useStatus'
import { STATUS } from '../../constants/Status.constants'

import translations from './Filters.translations'
import type OrderFilters from './Filters.types'

const props = withDefaults(
  defineProps<{
    availableMarkets?: Array<string>
    isAllTab?: boolean
    isPendingTab?: boolean
  }>(),
  {
    availableMarkets: () => [],
    isAllTab: false,
    isPendingTab: false,
  },
)
const emit = defineEmits<{
  (e: 'filtering', payload: OrderFilters): void
}>()
const i18n = useI18n()
const { KILL_REFURBISHER_LISTING_EXPENSIVE } = useRuntimeConfig().public
const { statusOptions, suspendReasonOptions } = useStatus()
const seeMoreFilters = ref(false)
const sourcingOrderId = ref('')
const trackingNumber = ref('')
const customer = ref('')
const creationDateMin = ref('')
const creationDateMax = ref('')
const countryCode = ref<string>('')
const status = ref<string>('')
const suspendReason = ref('')

const showFilterByName = computed(
  () => props.isAllTab && !KILL_REFURBISHER_LISTING_EXPENSIVE,
)
const showMoreFilters = computed(() => props.isAllTab && seeMoreFilters.value)
const showTrackingStatus = computed(
  () => showMoreFilters.value || props.isPendingTab,
)
const showSuspendReason = computed(
  () => showTrackingStatus.value && status.value === STATUS.SUSPENDED,
)
const filters = computed<OrderFilters>(() => ({
  ...insertIf(!isEmpty(countryCode.value), {
    countryCode: countryCode.value,
  }),
  ...insertIf(!isEmpty(customer.value), {
    customer: customer.value,
  }),
  ...insertIf(!isEmpty(creationDateMax.value), {
    creationDateMax: creationDateMax.value,
  }),
  ...insertIf(!isEmpty(creationDateMin.value), {
    creationDateMin: creationDateMin.value,
  }),
  ...insertIf(!isEmpty(sourcingOrderId.value), {
    sourcingOrderId: sourcingOrderId.value.toLocaleUpperCase(),
  }),
  ...insertIf(!isEmpty(status.value), {
    state: status.value,
  }),
  ...insertIf(!isEmpty(showSuspendReason.value && suspendReason.value), {
    suspendReason: suspendReason.value,
  }),
  ...insertIf(!isEmpty(trackingNumber.value), {
    trackingNumber: trackingNumber.value,
  }),
}))

const hasSomeFilters = computed(() => !isEmpty(filters.value))

function clearInputs() {
  countryCode.value = ''
  customer.value = ''
  creationDateMax.value = ''
  creationDateMin.value = ''
  sourcingOrderId.value = ''
  status.value = ''
  suspendReason.value = ''
  trackingNumber.value = ''
}

function toggleMoreFilters() {
  seeMoreFilters.value = !seeMoreFilters.value
}
async function applyFilters() {
  emit('filtering', filters.value)
}
async function resetFilters() {
  clearInputs()
  emit('filtering', filters.value)
}
</script>
